/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* Licensed under the MIT License. See LICENSE.md in the project root for license terms.
*--------------------------------------------------------------------------------------------*/

import debounce from "lodash.debounce";
import { editor } from "monaco-editor/esm/vs/editor/editor.api";
import { useEffect, useState } from "react";
import { FilePath, SourceFile } from "../../types";
import { Text } from "@itwin/itwinui-react";
import { DiagnosticList } from "./DiagnosticList";
import styles from "./ErrorList.module.css";

interface ErrorListProps {
  className?: string;
  sourceFiles?: SourceFile[];
  onErrors?: (errorCount: number) => void;
  onErrorClicked?: (filePath: FilePath, startLineNumber: number, endLineNumber: number, startColumn: number, endColumn: number) => void;
}

export const ErrorList = ({ sourceFiles, className, onErrors, onErrorClicked }: ErrorListProps) => {
  const [markerData, setMarkerData] = useState<{ [filePath: FilePath]: editor.IMarker[] }>({});
  const [selected, setSelected] = useState<editor.IMarker>();

  const groupfileMarkers = (markers: editor.IMarker[]) => {
    const _sourceFiles = sourceFiles ?? [];
    const fileMarkers: { [filePath: FilePath]: editor.IMarker[] } = {};
    const files = markers.map((data) => data.resource.path);
    files.sort();
    files.forEach((filePath) => {
      if (_sourceFiles.find((file) => (file.filePathRenamed ?? file.filePath) === filePath)) {
        fileMarkers[filePath] = markers.filter((data) => data.resource.path === filePath);
        fileMarkers[filePath] = fileMarkers[filePath].sort((a, b) => a.startLineNumber - b.startLineNumber);
      }
    })
    return fileMarkers;
  };

  const onClick = (marker: editor.IMarker) => {
    setSelected(marker);
    onErrorClicked && onErrorClicked(marker.resource.path, marker.startLineNumber, marker.endLineNumber, marker.startColumn, marker.endColumn);
  }

  useEffect(() => {
    const subscription = editor.onDidChangeMarkers(debounce(() => {
      const markers = groupfileMarkers(editor.getModelMarkers({ owner: "typescript" }));
      setMarkerData(markers);
      const errorCount = Object.values(markers).reduce((prev, curr) => prev + curr.length, 0);
      onErrors && onErrors(errorCount);
    }, 300, { leading: true }));

    return () => subscription?.dispose();
  }, [groupfileMarkers]);

  if (!Object.keys(markerData).length)
    return (
      <div className={[className ? className : "", styles.centered].join(" ")}>
        <Text isMuted>No problems have been detected so far</Text>
      </div>
    )

  return (
    <div>
      {Object.entries(markerData).map(([filePath, markers]) => (
        <DiagnosticList
          key={filePath}
          file={filePath}
          markers={markers}
          markerSelected={selected}
          onClick={onClick}
        />
      ))}
    </div>
  );
};